<script setup lang="ts">
import { computed, ref } from 'vue';

import MeService from '@/core/shared/me/me.service';
import {
  isTeamOwnedTemplate,
  isTemplateOwnedBy,
} from '@/core/copilot-configuration-template/utils/template-ownership.util';

import type { CopilotConfigurationTemplate } from '@factoryfixinc/ats-interfaces';

import CopilotPersonalityCard from '@/components/CopilotPersonalities/CopilotPersonalityCard/CopilotPersonalityCard.vue';
import SpinnerLoader from '@/components/Shared/Loaders/SpinnerLoader.vue';
import { getUserName, getUserShortName } from '@/core/shared/user-profile/utils';

const PersonalityColors = ['green', 'blue', 'ruby', 'purple'];

enum OwnerFilter {
  MY_PERSONALITIES = 'my',
  TEAM_PERSONALITIES = 'team',
}

const ffCopilotDefaultTemplate = {
  copilotConfigurationTemplate: {
    id: 0,
    displayName: 'Copilot',
    description: 'AI-powered recruiter for skilled trades and manufacturing roles',
    ownerId: 0,
    employerId: 0,
    version: 'v1',
    snapshot: '{}',
    createTs: '2025-01-01',
    updateTs: '2021-01-01',
  },
  activeJobCount: 0,
  activityMessage: '',
  isDefault: true,
  baseColor: 'ff-gradient' as const,
  ownerName: 'FactoryFix',
  isOwnedByCurrentUser: false,
  lastEditedByName: '',
  isLastEditedByCurrentUser: false,
  isEditable: false,
  isTransferable: false,
  showActiveJobCount: false,
};

// Props
// ===================
const props = defineProps<{
  copilotConfigurationTemplates: CopilotConfigurationTemplate[];
  ownerId: number;
  isLoading?: boolean;
  /** Live jobs on the default Copilot; null (not loaded) hides the default card's job count. */
  defaultCopilotActiveJobCount?: number | null;
}>();

// Emits
// ===================
const emit = defineEmits<{
  (e: 'edit-template', id: number): void;
  (e: 'delete-template', id: number): void;
  (e: 'transfer-template', id: number): void;
}>();

// Services
// ===================
const meService = new MeService();

// Reactive state
// ===================
const ownerFilter = ref<OwnerFilter>(OwnerFilter.MY_PERSONALITIES);

// Computed Properties
// ===================
const isLoading = computed(() => props.isLoading ?? false);

const isMyRecruitersFilterSelected = computed(() => {
  return ownerFilter.value === OwnerFilter.MY_PERSONALITIES;
});

const isTeamRecruitersFilterSelected = computed(() => {
  return ownerFilter.value === OwnerFilter.TEAM_PERSONALITIES;
});

const isEmployerAdmin = computed(() => meService.isEmployerAdmin);

/**
 * Owner always; Company Admins may edit/delete any recruiter (person- or team-owned).
 * Team-owned recruiters have no owner, so only the admin branch applies.
 */
const canManageTemplate = (template: CopilotConfigurationTemplate): boolean => {
  if (isTemplateOwnedBy(template, props.ownerId)) {
    return true;
  }

  return isEmployerAdmin.value;
};

const userNameById = (userId: number | null | undefined): string => {
  if (userId === null || userId === undefined) {
    return '';
  }

  const user = meService.employerUsers?.find((employerUser) => employerUser.id === userId);
  return user ? getUserName(user) : '';
};

const userShortNameById = (userId: number | null | undefined): string => {
  if (userId === null || userId === undefined) {
    return '';
  }

  const user = meService.employerUsers?.find((employerUser) => employerUser.id === userId);
  return user ? getUserShortName(user) : '';
};

// TODO: remove projectCount typing once ats-interfaces 3.0.58 is released
const filteredCopilotConfigurationTemplates = computed<
  Array<CopilotConfigurationTemplate & { projectCount?: number }>
>(() => {
  // Team-owned recruiters (no owner) always belong under the Team tab.
  return props.copilotConfigurationTemplates.filter((template) => {
    const isMine = isTemplateOwnedBy(template, props.ownerId);
    return ownerFilter.value === OwnerFilter.MY_PERSONALITIES ? isMine : !isMine;
  });
});

const personalityCardListData = computed(() => {
  const filteredCopilots = filteredCopilotConfigurationTemplates.value.map((template, index) => {
    const ownerName = isTeamOwnedTemplate(template) ? '' : userNameById(template.ownerId);

    return {
      copilotConfigurationTemplate: template,
      activeJobCount: template.projectCount ?? 0,
      activityMessage: '',
      isDefault: false,
      baseColor: PersonalityColors[index % PersonalityColors.length] as
        | 'green'
        | 'blue'
        | 'ruby'
        | 'purple',
      ownerName: ownerName,
      isOwnedByCurrentUser: isTemplateOwnedBy(template, props.ownerId),
      lastEditedByName: userShortNameById(template.lastEditedById),
      isLastEditedByCurrentUser:
        template.lastEditedById !== null &&
        template.lastEditedById !== undefined &&
        template.lastEditedById === props.ownerId,
      isEditable: canManageTemplate(template),
      isTransferable: canManageTemplate(template),
      showActiveJobCount: true,
    };
  });

  const defaultCount = props.defaultCopilotActiveJobCount;
  const defaultCard = {
    ...ffCopilotDefaultTemplate,
    activeJobCount: defaultCount ?? 0,
    showActiveJobCount: defaultCount !== null && defaultCount !== undefined,
  };

  return [defaultCard, ...filteredCopilots];
});

// Methods
// ===================
const handleCardEdit = (templateId: number) => {
  emit('edit-template', templateId);
};

const handleCardDelete = (templateId: number) => {
  emit('delete-template', templateId);
};

const handleCardTransfer = (templateId: number) => {
  emit('transfer-template', templateId);
};
</script>

<template>
  <div>
    <!-- Filters -->
    <div class="mb-8 flex items-center justify-start border-b-[0.5px] border-black/10">
      <!-- My recruiters -->
      <div
        :class="{
          'flex min-h-[44px] cursor-pointer items-center border-b-2 pb-2 text-sm sm:block sm:min-h-0': true,
          'mr-6': true,
          'border-highlight-600 font-bold text-highlight-500': isMyRecruitersFilterSelected,
          'border-transparent text-shade-800': !isMyRecruitersFilterSelected,
        }"
        @click="ownerFilter = OwnerFilter.MY_PERSONALITIES"
      >
        My recruiters
      </div>
      <!-- Team recruiters -->
      <div
        :class="{
          'flex min-h-[44px] cursor-pointer items-center border-b-2 pb-2 text-sm sm:block sm:min-h-0': true,
          'mr-6': true,
          'border-highlight-600 font-bold text-highlight-500': isTeamRecruitersFilterSelected,
          'border-transparent text-shade-800': !isTeamRecruitersFilterSelected,
        }"
        @click="ownerFilter = OwnerFilter.TEAM_PERSONALITIES"
      >
        Team recruiters
      </div>
    </div>
    <!-- Cards -->
    <div class="flex flex-wrap justify-start gap-4 sm:gap-8">
      <!-- Loading state -->
      <div v-if="isLoading" class="flex w-full items-center justify-center">
        <SpinnerLoader class="mt-[250px] h-[68px] w-[68px]"></SpinnerLoader>
      </div>
      <CopilotPersonalityCard
        v-else
        v-for="card in personalityCardListData"
        :key="card.copilotConfigurationTemplate.id"
        :copilot-configuration-template="card.copilotConfigurationTemplate"
        :active-job-count="card.activeJobCount"
        :activity-message="card.activityMessage"
        :is-default="card.isDefault"
        :base-color="card.baseColor"
        :owner-name="card.ownerName"
        :is-owned-by-current-user="card.isOwnedByCurrentUser"
        :last-edited-by-name="card.lastEditedByName"
        :is-last-edited-by-current-user="card.isLastEditedByCurrentUser"
        :is-editable="card.isEditable"
        :is-transferable="card.isTransferable"
        :show-active-job-count="card.showActiveJobCount"
        @edit="handleCardEdit(card.copilotConfigurationTemplate.id)"
        @delete="handleCardDelete(card.copilotConfigurationTemplate.id)"
        @transfer-ownership="handleCardTransfer(card.copilotConfigurationTemplate.id)"
      />
    </div>
  </div>
</template>
